// debugLog — a lightweight in-browser capture buffer so a tester can
// open one panel, copy a text snapshot, and hand it to whoever is
// debugging. Captures: every fetch (method / path / status / ms /
// error / still-pending), console.error + console.warn, uncaught
// errors, and unhandled promise rejections.
//
// Why this exists: the DreamBook stage flow is tested by a human in a
// browser while the engineer has no browser tooling. Screenshot-by-
// screenshot is slow and loses the network/console truth. This buffer
// makes the client-side state copy-pasteable in one click.
//
// Install once, as early as possible, from main.tsx.

import { APP_VERSION } from "./version";

export interface FetchEntry {
  kind: "fetch";
  t: number;             // epoch ms when the request started
  method: string;
  url: string;
  status: number | null; // null while pending
  ms: number | null;     // null while pending
  ok: boolean | null;
  error: string | null;  // network error / abort message
  pending: boolean;
}

export interface LogEntry {
  kind: "error" | "warn";
  t: number;
  text: string;
}

type Entry = FetchEntry | LogEntry;

const MAX = 80;
const buffer: Entry[] = [];
let installed = false;

function push(e: Entry) {
  buffer.push(e);
  if (buffer.length > MAX) buffer.splice(0, buffer.length - MAX);
}

/** Strip the origin + query so the buffer shows compact, readable paths. */
function shortPath(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname + (u.search ? "?…" : "");
  } catch {
    return url;
  }
}

export function installDebugCapture(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // ---- fetch ----
  const origFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const method = (init?.method
      || (typeof input !== "string" && !(input instanceof URL) ? input.method : null)
      || "GET").toUpperCase();
    const rawUrl = typeof input === "string" ? input
      : input instanceof URL ? input.toString()
      : input.url;
    const entry: FetchEntry = {
      kind: "fetch", t: Date.now(), method, url: shortPath(rawUrl),
      status: null, ms: null, ok: null, error: null, pending: true,
    };
    push(entry);
    const started = performance.now();
    try {
      const res = await origFetch(input, init);
      entry.status = res.status;
      entry.ok = res.ok;
      entry.ms = Math.round(performance.now() - started);
      entry.pending = false;
      return res;
    } catch (err) {
      entry.error = err instanceof Error ? err.message : String(err);
      entry.ms = Math.round(performance.now() - started);
      entry.ok = false;
      entry.pending = false;
      throw err;
    }
  };

  // ---- console.error / console.warn ----
  const origErr = console.error.bind(console);
  const origWarn = console.warn.bind(console);
  console.error = (...args: unknown[]) => {
    push({ kind: "error", t: Date.now(), text: args.map(stringifyArg).join(" ") });
    origErr(...args);
  };
  console.warn = (...args: unknown[]) => {
    push({ kind: "warn", t: Date.now(), text: args.map(stringifyArg).join(" ") });
    origWarn(...args);
  };

  // ---- uncaught errors + unhandled rejections ----
  window.addEventListener("error", (e) => {
    push({ kind: "error", t: Date.now(), text: `[uncaught] ${e.message}` });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason instanceof Error ? e.reason.message : String(e.reason);
    push({ kind: "error", t: Date.now(), text: `[unhandled rejection] ${reason}` });
  });
}

/** Push a synthetic breadcrumb into the buffer. Used to instrument
 *  component lifecycle / state transitions that don't surface as a
 *  fetch or a console error — e.g. "modal open=true showing=false".
 *  Shows in the debug panel as a MARK line. */
export function debugMark(text: string): void {
  push({ kind: "warn", t: Date.now(), text: `[mark] ${text}` });
}

function stringifyArg(a: unknown): string {
  if (typeof a === "string") return a;
  if (a instanceof Error) return a.message;
  try { return JSON.stringify(a); } catch { return String(a); }
}

/** Live entries, oldest first. The panel reads this on a poll. */
export function getDebugEntries(): readonly Entry[] {
  return buffer;
}

/** A copy-pasteable plain-text snapshot — version, time, then the
 *  whole buffer newest-last. This is what the tester hands back. */
export function getDebugSnapshot(): string {
  const now = new Date();
  const lines: string[] = [
    `DreamBook debug snapshot`,
    `app version : v${APP_VERSION}`,
    `taken at    : ${now.toISOString()}`,
    `url         : ${window.location.pathname + window.location.search}`,
    `user agent  : ${navigator.userAgent}`,
    `entries     : ${buffer.length}`,
    `${"-".repeat(60)}`,
  ];
  for (const e of buffer) {
    const ts = new Date(e.t).toISOString().slice(11, 23);
    if (e.kind === "fetch") {
      const st = e.pending ? "PENDING" : e.error ? `ERR(${e.error})` : String(e.status);
      const ms = e.ms == null ? "—" : `${e.ms}ms`;
      lines.push(`${ts}  ${e.method.padEnd(5)} ${st.padEnd(10)} ${ms.padStart(7)}  ${e.url}`);
    } else {
      lines.push(`${ts}  ${e.kind.toUpperCase()}  ${e.text}`);
    }
  }
  return lines.join("\n");
}
